/**
 * formatAnswer
 * ------------
 * Converts the raw value from `formData` into a human-readable string.
 *
 * • Handles translations for option labels (`labelKey`) and question titles
 *   via the supplied `t` function (optional).
 * • Falls back gracefully when a translation or label is missing.
 *
 * @param {object}  question  – Question definition (may contain `titleKey`,
 *                              `descriptionKey`, option `labelKey`, etc.).
 * @param {object}  formData  – Answers keyed by question.id.
 * @param {Function} [t]      – i18next translate fn (pass from the caller).
 * @returns {string}            Localised, human-friendly string.
 */
// utils/form/formatAnswer.js
export default function formatAnswer(question, formData, t = (k) => k) {
  const raw = formData?.[question.id];

  if (raw == null || raw === "") return t("common.na") ?? "N/A";

  switch (question.fieldType) {
    case "date": {
      const d = new Date(raw);
      return Number.isNaN(d) ? String(raw) : d.toLocaleDateString();
    }

    case "radio":
    case "select": {
      const opt = question.options?.find(
        (o) => String(o.value) === String(raw)
      );
      if (!opt) return String(raw);
      return opt.labelKey ? t(opt.labelKey) : opt.label ?? String(raw);
    }

    case "boolean-matrix": {
      const yes = t("sgrq.scales.yesNo.true") || "True";
      const no = t("sgrq.scales.yesNo.false") || "False";
      const rows = question.rows || [];
      const lines = rows.map((r) => {
        const v = raw?.[r.id];
        const txt =
          v === true ? yes : v === false ? no : t("common.na") ?? "N/A";
        const label = t(r.labelKey ?? r.label ?? r.id);
        return `• ${label}: ${txt}`;
      });
      return lines.join("\n"); // always a string
    }

    case "range":
    case "number":
    case "textarea":
    case "text":
      return String(raw);

    default:
      return typeof raw === "object" ? JSON.stringify(raw) : String(raw);
  }
}
